import store from "@plugins/store";
import i18n, { isLocaleReady } from "@plugins/i18n";
import { i18nUtils } from "@utils/i18n";
import { isDefined, isEmpty } from "@utils/var.check";

const previous = [];
// due to SLSSIT-4503, need this flag
let backOrForwardNavigation = false;
// SLSUAT-7076, annotation card content subpage close after query change
let preventContentSubpageClose = false;
window.addEventListener("popstate", () => {
  backOrForwardNavigation = true;
});

// ── Route meta utilities ──────────────────────────────────────────────────────

/**
 * Utilitiy method to get attribute from route meta
 */
export function getRouteMetaField(to, fieldName) {
  let field;
  to.matched.some((m) => {
    field = m.meta[fieldName];
  });
  return field;
}

/**
 * Utilitiy method to get meta fields from matched routes from last to first
 */
export function getNearestRouteMetaField(to, fieldName) {
  let field;
  to.matched
    .slice()
    .reverse()
    .find((m) => {
      if (m.meta && m.meta[fieldName]) {
        field = m.meta[fieldName];
      }
    });
  return field;
}

// ── Navigation state ──────────────────────────────────────────────────────────

/** Returns true when the browser fired a popstate event AND the destination matches the previous route. */
export function isBackNavigation(to): boolean {
  return isBackEvent(to) && backOrForwardNavigation;
}

export function getPreventContentSubpageClose(): boolean {
  return preventContentSubpageClose;
}

/** Resets per-navigation flags — call from router afterEach. */
export function resetNavigationFlags(): void {
  backOrForwardNavigation = false;
  preventContentSubpageClose = false;
}

export function setPreventContentSubpageClose(prevent): void {
  preventContentSubpageClose = prevent;
}

// ── Route navigation ──────────────────────────────────────────────────────────

export function handleRouteNext(to, from, next) {
  const isFromEAssessment = isEAssessmentRoute(from.path);
  const isToEAssessment = isEAssessmentRoute(to.path);

  if (isFromEAssessment && isToEAssessment) {
    routeNext(to, from, next);
  } else if (isFromEAssessment) {
    let path: string;

    if (!isEmpty(to.query)) {
      const queryString = new URLSearchParams(to.query).toString();
      path = `${to.path}?${queryString}`;
    } else {
      path = to.path;
    }

    window.location.href = path;
  } else {
    routeNext(to, from, next);
  }
}

export function routeNext(to, from, next, toPath?) {
  // same route path navigate (eg. quiz navigate between quizPage)
  const sameRoute = to.path === from.path;
  if (sameRoute && !isDefined(toPath)) {
    next();
    return;
  }
  isDirty().then((confirmDiscard) => {
    if (confirmDiscard) {
      store.dispatch("app/clearPageDirty");
      if (toPath) {
        return next(toPath);
      } else {
        const pageTitle = getNearestRouteMetaField(to, "title");
        const pageMetaTags = getNearestRouteMetaField(to, "metaTags");

        setDocumentTitle(pageTitle);
        setDocumentMetaTags(pageMetaTags);
        store.dispatch("app/setCurrentPage", {
          pageTitle,
          pageMetaTags,
        });
        if (isBackEvent(to)) {
          previous.splice(-1, 1);
        } else {
          previous.push(from);
        }
        return next();
      }
    } else {
      next(false);
    }
  });
}

// ── Private helpers ───────────────────────────────────────────────────────────

function isEAssessmentRoute(path) {
  const E_ASSESSMENT_ROUTES = ["/assessment", "/class-group/assessment"];
  return E_ASSESSMENT_ROUTES.some((route) => path.startsWith(route));
}

function isBackEvent(to) {
  if (previous.length === 0) {
    return false;
  }

  const previousPage = previous[previous.length - 1];

  const isSameParams =
    Object.keys(previousPage.params).length === Object.keys(to.params).length &&
    Object.keys(previousPage.params).every((key) => previousPage.params[key] === to.params[key]);

  const isSameQuery =
    Object.keys(previousPage.query).length === Object.keys(to.query).length &&
    Object.keys(previousPage.query).every((key) => previousPage.query[key] === to.query[key]);

  return previousPage.path === to.path && isSameParams && isSameQuery;
}

function isDirty() {
  return new Promise((resolve) => {
    if (store.getters["app/checkWholePageDirty"]) {
      if (!confirm(i18n.t("dirty_confirmation_prompt"))) {
        resolve(false);
      } else {
        resolve(true);
      }
    } else {
      resolve(true);
    }
  });
}

function setDocumentMetaTags(pageMetaTags) {
  // Remove old meta tags from the document
  document
    .querySelectorAll("[data-vue-router-controlled]")
    .forEach((el) => el.parentNode.removeChild(el));

  // Create meta tag elements and append to head.
  if (pageMetaTags) {
    pageMetaTags.forEach((tagDef) => {
      const tag = document.createElement("meta");
      Object.keys(tagDef).forEach((key) => {
        tag.setAttribute(key, tagDef[key]);
      });
      tag.setAttribute("data-vue-router-controlled", "");
      document.head.appendChild(tag);
    });
  }
}

function setDocumentTitle(pageTitle) {
  if (pageTitle) {
    isLocaleReady().then(() => {
      i18nUtils.setDocumentTitle(i18n.t(pageTitle));
    });
  }
}
